/**
 * Les coordonnées du responsable, telles que l'écran « Vos coordonnées » les
 * demande : exactement les champs que `completudeProfil` exige avant une
 * inscription — plus, pour un parent, ce qu'il faut à l'attestation fiscale.
 *
 * ## Trois sortes de comptes (VALIDÉ ÉCLOSION, 22/10/2026)
 *
 * - **parent** : père, mère, tuteur légal. Le seul à recevoir l'attestation
 *   fiscale, donc le seul à qui l'on demande son registre national.
 * - **other** : un proche qui inscrit l'enfant — grand-parent, famille
 *   d'accueil, oncle, ami de la famille. Une personne, pas une organisation :
 *   mêmes coordonnées qu'un parent, pas de nom d'organisation, pas
 *   d'attestation fiscale, donc pas de registre national.
 * - **organisation** : école, CPAS, maison d'accueil, service d'aide à la
 *   jeunesse. Un nom (obligatoire), un numéro d'entreprise (facultatif, mais
 *   vérifié), et une personne de contact.
 *
 * Le type décide aussi du tiers en comptabilité (`famille` ou `autre`,
 * VALIDÉ ÉCLOSION) et du nom porté par les factures : d'où l'intérêt de le
 * demander en premier, avant que quoi que ce soit ne soit facturé.
 *
 * ## La date de naissance du parent n'est pas stockée
 *
 * Elle sert à proposer les six premiers chiffres du registre national et à
 * vérifier que le numéro saisi est bien le sien ; le numéro la contient déjà.
 * La table `users` n'a pas de colonne pour elle, et c'est très bien ainsi.
 */

import { chiffresNRN, erreurNRN, prefixeNRN, ageA, AGE_MIN_RESPONSABLE } from './registreNational';

export type Erreurs = Record<string, string>;

export type TypeDeCompte = 'parent' | 'organisation' | 'other';

/*
  « Je suis… », pas « vous inscrivez… » : la première version disait « Mes
  enfants » puis « Un enfant de ma famille », et un parent pressé lisait
  « plusieurs enfants » contre « un seul ». L'ordre suit la fréquence (parent,
  organisation, proche) et chaque choix dit ce qu'il vaut pour l'attestation
  fiscale, la seule chose qui change pour la famille.
*/
export const TYPES_DE_COMPTE: { valeur: TypeDeCompte; libelle: string; detail: string }[] = [
  {
    valeur: 'parent',
    libelle: 'Un parent qui inscrit son ou ses enfants',
    detail: 'Père, mère ou tuteur légal. L’attestation fiscale est établie à votre nom.',
  },
  {
    valeur: 'organisation',
    libelle: 'Une organisation',
    detail: 'École, CPAS, maison d’accueil, service d’aide à la jeunesse… Pas d’attestation fiscale.',
  },
  {
    valeur: 'other',
    libelle: 'Un aidant proche',
    detail: 'Grand-parent, famille d’accueil, oncle, tante, ami de la famille… Vous inscrivez l’enfant, sans attestation fiscale.',
  },
];

export const estOrganisation = (type: string | null | undefined) => type === 'organisation';

export interface Coordonnees {
  account_type: TypeDeCompte;
  organisation_name: string;
  company_number: string;
  prenom: string;
  nom: string;
  adresse: string;
  cpostal: string;
  localite: string;
  telephone: string;
  /** Parent seulement. `AAAA-MM-JJ`, saisie pour aider et vérifier, jamais stockée. */
  date_naissance: string;
  /** Parent seulement. */
  nnational: string;
}

export const COORDONNEES_VIDES: Coordonnees = {
  account_type: 'parent',
  organisation_name: '',
  company_number: '',
  prenom: '',
  nom: '',
  adresse: '',
  cpostal: '',
  localite: '',
  telephone: '',
  date_naissance: '',
  nnational: '',
};

/* -------------------------------------------------------------------------- */
/* Le numéro d'entreprise                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Le numéro d'entreprise (BCE), ramené à ses dix chiffres : « BE 0123.456.789 »
 * et « 0123456789 » sont le même numéro. `null` si ce n'en est pas un.
 *
 * Dix chiffres, le premier 0 ou 1, les deux derniers = 97 − (les huit
 * premiers mod 97). Un ancien numéro à neuf chiffres se lit avec un 0 devant.
 */
export function numeroEntreprise(brut: string): string | null {
  let chiffres = (brut ?? '').replace(/^\s*BE/i, '').replace(/\D/g, '');
  if (chiffres.length === 9) chiffres = `0${chiffres}`;
  if (!/^[01]\d{9}$/.test(chiffres)) return null;
  const corps = parseInt(chiffres.slice(0, 8), 10);
  const cle = parseInt(chiffres.slice(8), 10);
  return 97 - (corps % 97) === cle ? chiffres : null;
}

/** « 0123456789 » → « 0123.456.789 », la forme imprimée. */
export const numeroEntrepriseLisible = (chiffres: string) =>
  `${chiffres.slice(0, 4)}.${chiffres.slice(4, 7)}.${chiffres.slice(7)}`;

/* -------------------------------------------------------------------------- */
/* Le registre national du parent                                              */
/* -------------------------------------------------------------------------- */

/**
 * Le numéro à retenir : les six chiffres de la date, proposés à l'entrée du
 * champ, ne sont pas un numéro — laissés seuls, ils valent « non renseigné ».
 */
export function nrnParentRetenu(v: Pick<Coordonnees, 'nnational' | 'date_naissance'>): string {
  const nrn = chiffresNRN(v.nnational);
  return nrn && nrn === prefixeNRN(v.date_naissance) ? '' : nrn;
}

export interface ContexteCoordonnees {
  /** Les numéros de registre national des enfants du compte. */
  nrnDesEnfants?: string[];
  aujourdhui?: Date;
}

/* -------------------------------------------------------------------------- */
/* Les règles de l'écran                                                       */
/* -------------------------------------------------------------------------- */

/** Les règles de l'écran : celles de `completudeProfil`, dites en clair. */
export function erreursCoordonnees(v: Coordonnees, contexte: ContexteCoordonnees = {}): Erreurs {
  const e: Erreurs = {};
  const aujourdhui = contexte.aujourdhui ?? new Date();

  if (estOrganisation(v.account_type)) {
    if (!v.organisation_name.trim()) e.organisation_name = 'Le nom de l’organisation est requis.';
    if (v.company_number.trim() && !numeroEntreprise(v.company_number)) {
      e.company_number = 'Ce numéro d’entreprise n’est pas valable (dix chiffres, par exemple 0123.456.789).';
    }
  }

  if (!v.prenom.trim()) e.prenom = 'Votre prénom est requis.';
  if (!v.nom.trim()) e.nom = 'Votre nom est requis.';
  if (!v.adresse.trim()) e.adresse = 'Votre adresse est requise.';
  if (!v.cpostal.trim()) e.cpostal = 'Le code postal est requis.';
  else if (!/^\d{4}$/.test(v.cpostal.trim())) e.cpostal = 'Un code postal belge compte 4 chiffres.';
  if (!v.localite.trim()) e.localite = 'La localité est requise.';
  if (!v.telephone.trim()) e.telephone = 'Votre téléphone est requis.';
  else if (v.telephone.replace(/\D/g, '').length < 9) e.telephone = 'Ce numéro semble incomplet.';

  if (v.account_type === 'parent') {
    let dateSure: string | undefined;
    if (v.date_naissance.trim()) {
      const d = new Date(v.date_naissance);
      if (Number.isNaN(d.getTime()) || !/^\d{4}-\d{2}-\d{2}$/.test(v.date_naissance)) {
        e.date_naissance = 'Cette date n’est pas valable.';
      } else if (v.date_naissance > aujourdhui.toISOString().slice(0, 10)) {
        e.date_naissance = 'Cette date est dans le futur.';
      } else if (ageA(v.date_naissance, aujourdhui) < AGE_MIN_RESPONSABLE) {
        e.date_naissance = 'Cette date semble être celle d’un enfant : nous attendons la vôtre.';
      } else {
        dateSure = v.date_naissance;
      }
    }

    const nrn = nrnParentRetenu(v);
    if (nrn) {
      const probleme = erreurNRN(nrn, {
        qui: 'parent',
        dateNaissance: dateSure,
        interdits: contexte.nrnDesEnfants,
        aujourdhui,
      });
      if (probleme) e.nnational = probleme;
    }
  }

  return e;
}

/* -------------------------------------------------------------------------- */
/* Vers la base                                                                */
/* -------------------------------------------------------------------------- */

/** Les colonnes de `users` que cet écran écrit. */
export interface ProfilEcrit {
  account_type: TypeDeCompte;
  organisation_name: string | null;
  company_number: string | null;
  prenom: string;
  nom: string;
  adresse: string;
  cpostal: string;
  localite: string;
  telephone: string;
  nnational?: string | null;
  is_legal_guardian?: boolean;
}

/**
 * Ce qui part en base.
 *
 * Un compte qui n'est pas une organisation ne garde ni nom d'organisation ni
 * numéro d'entreprise : la donnée survivrait au changement de type et
 * ressortirait sur une facture. Un compte qui n'est pas un parent ne garde
 * pas de registre national et n'est pas tuteur légal : pas d'attestation
 * fiscale pour lui. Pour un parent, `is_legal_guardian` n'est pas touché.
 */
export function profilDepuisCoordonnees(v: Coordonnees): ProfilEcrit {
  const organisation = estOrganisation(v.account_type);
  const bce = organisation ? numeroEntreprise(v.company_number) : null;
  return {
    account_type: v.account_type,
    organisation_name: organisation ? v.organisation_name.trim() : null,
    company_number: bce ? numeroEntrepriseLisible(bce) : null,
    prenom: v.prenom.trim(),
    nom: v.nom.trim(),
    adresse: v.adresse.trim(),
    cpostal: v.cpostal.trim(),
    localite: v.localite.trim(),
    telephone: v.telephone.trim(),
    ...(v.account_type === 'parent'
      ? { nnational: nrnParentRetenu(v) || null }
      : { nnational: null, is_legal_guardian: false }),
  };
}
